import { ChevronRight, Lock } from 'react-feather';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useModal } from 'react-modal-hook';

import { useAuth, useLogout } from '../../common/Auth/useAuth';
import { useParticipant } from './ParticipantProvider';
import LoginForm from '../Common/LoginForm';
import UI from '../../common/UI';

interface ScopedParticipant {
  id: string;
  email?: string;
}

/**
 * Returns a callback that will only be executed if the currently
 * authenticated user's email matches the given email.
 */
export const useGuard = (
  scopedParticipant?: ScopedParticipant | null, scopedEmail?: string | null, allowSignup?: boolean,
) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { participant, refetch } = useParticipant();

  const isAuthorizedParticipant = !scopedParticipant || participant?.id === scopedParticipant.id;
  const isAuthorizedEmail = !scopedEmail || user?.email.toLowerCase() === scopedEmail.toLowerCase();
  const isAuthorized = !!user && isAuthorizedParticipant && isAuthorizedEmail;

  const [callback, setCallback] = useState<() => void>();

  const [openModal, closeModal] = useModal(() => {
    const close = () => {
      setCallback(undefined);
      closeModal();
    };

    const onCompleted = () => {
      refetch();
      closeModal();
    };

    return (
      <UI.Modal maxWidth={500} close={close}>
        <LoginForm
          title={scopedParticipant ? t('confirm_it_is_you') : null}
          description={scopedParticipant ? t('login_to_continue_description') : null}
          scopedParticipantId={scopedParticipant?.id}
          scopedEmail={scopedEmail}
          onCompleted={onCompleted}
          allowSignup={allowSignup}
        />
      </UI.Modal>
    );
  }, [scopedParticipant?.id, allowSignup, refetch]);

  /**
   * If the participant is authorized, calls the callback function.
   * Otherwise, shows a login modal and then calls the callback function.
   */
  const guard = useCallback((fn: () => void) => {
    if (!isAuthorized) {
      // Need to use arrow function, otherwise the SetStateAction will actually execute the fn.
      setCallback(() => fn);
      openModal();
    } else {
      fn();
    }
  }, [isAuthorized, openModal]);

  // Execute the callback when authorized.
  useEffect(() => {
    if (callback && isAuthorized) {
      callback();
      closeModal();
    }
  }, [callback, isAuthorized, closeModal]);

  return {
    guard,
    isAuthorized,
    isAuthorizedParticipant,
    isAuthorizedEmail,
  };
};

export interface GuardProps {
  scopedParticipant?: ScopedParticipant | null;
  scopedEmail?: string | null;
  showModal?: boolean;
  allowSignup?: boolean;
  children?: ReactNode;
  renderAuthenticated?: () => ReactNode;
}

const Guard = ({
  scopedParticipant,
  scopedEmail,
  showModal = true,
  allowSignup = false,
  children,
  renderAuthenticated,
}: GuardProps) => {
  const { t } = useTranslation();
  const { guard, isAuthorized, isAuthorizedEmail } = useGuard(scopedParticipant, scopedEmail, allowSignup);
  const { user } = useAuth();

  const [logout] = useLogout({
    redirectToLoginPage: false,
  });

  const [showChildren, setShowChildren] = useState(isAuthorized);

  useEffect(() => {
    if (!isAuthorized && showModal) {
      guard(() => setShowChildren(true));
    }
  }, [isAuthorized, showModal, guard]);

  if (!showChildren) {
    if (user && scopedEmail && !isAuthorizedEmail) {
      return (
        <UI.Warning>
          <Trans
            i18nKey="guard.log_in_as_correct_user_warning"
            values={{
              scopedEmail,
              currentEmail: user.email,
            }}
          >
            <UI.Strong />
          </Trans>
          {' '}
          <UI.A onClick={() => logout()} role="button">
            {t('log_out')}
          </UI.A>
        </UI.Warning>
      );
    }

    if (allowSignup) {
      return (
        <UI.Div>
          <UI.Paragraph>
            {t('guard.log_in_or_sign_up_to_continue')}
          </UI.Paragraph>
          <UI.Button onClick={() => guard(() => setShowChildren(true))} sc={{ brand: 'secondary', block: true }}>
            {t('log_in_or_sign_up')}
            {' '}
            <UI.Icon>
              <ChevronRight />
            </UI.Icon>
          </UI.Button>
        </UI.Div>
      );
    }

    return (
      <UI.Div sc={{ textAlign: 'center' }}>
        <UI.A role="button" onClick={() => guard(() => setShowChildren(true))}>
          <UI.Icon>
            <Lock />
          </UI.Icon>
          {' '}
          {t('guard.log_in_to_view_this_section')}
        </UI.A>
      </UI.Div>
    );
  }

  return (
    <>
      {renderAuthenticated?.() || children}
    </>
  );
};

export default Guard;
